import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import { withStyles } from '@material-ui/core/styles';
import styles from './RecordsDocumentsDialog.style';
import {
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import EnhancedTable from '../../../Shared/EnhancedTable/EnhancedTable';
import { getExpertRecordDocuments } from '../../../../Services/DepositoryService';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {
  downloadFile,
  viewPdfInNewTab,
  getRecordFileUrl,
} from '../../../../Services/FileService';
import isPdf from '../../../../Utilities/isPdf';
import moment from 'moment';

const headCells = [
    { id: 'fileName', label: 'File Name', width: '25%' },
    { id: 'fileType', label: 'Document Type', width: '25%' },
    { id: 'sharedBy', label: 'Shared By', width: '12%' },
    { id: 'sharedAt', label: 'Share Date', width: '12%', isDate: true },
    { id: 'lastReviewedAt', label: 'Last Reviewed', width: '12%', isDate: true },
    {
      id: 'expiresAt',
      label: 'Expiration Date',
      width: '12%',
      isDate: true
    },
    { id: 'actions', label: 'Actions', width: '14%' }
  ];

// action buttons
function ViewButton(props) {
  const { classes, row, setRow, isDownloadAllowed, recordsInfo } = props;
  const [btnDownloading, setBtnDownloading] = useState(false);
  const [completedDownload, setCompletedDownload] = useState(0);
  const [variantDownload, setVariantDownload] = useState('indeterminate');
  const [isPdfFile, setIsPdfFile] = useState(false);
  const [disableDownloadButton, setDisableDownloadButton] = useState(false);
  const [isViewingFile, setIsViewingFile] = useState(false);
  const fileName = row ? row['fileName'] : '';

  useEffect(() => {
    const PdfFile = isPdf(fileName);
    const disableDownloadButton = !isDownloadAllowed && PdfFile;

    setIsPdfFile(PdfFile);
    setDisableDownloadButton(disableDownloadButton);
  }, [fileName, isDownloadAllowed]);

  const onDownloadButtonClick = async () => {
    if (disableDownloadButton) return;

    try {
      // Get the file URL first
      const url = await getRecordFileUrl(
        recordsInfo.orderId,
        recordsInfo.orderRecordId,
        row.id
      );

      // Download the file
      await downloadFile(
        url,
        row.fileName,
        btnDownloading,
        setBtnDownloading,
        setVariantDownload,
        setCompletedDownload,
        row,
        'lastReviewedAt',
        setRow
      );
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const onOpenButtonClick = async () => {
    if (!isPdfFile) return;

    try {
      // Get the file URL first
      const url = await getRecordFileUrl(
        recordsInfo.orderId,
        recordsInfo.orderRecordId,
        row.id
      );

      // View the PDF in a new tab
      await viewPdfInNewTab(url, setIsViewingFile, row.fileName);

      // Update lastReviewedAt if needed
      if (row && setRow) {
        const updatedRow = { ...row };
        updatedRow.lastReviewedAt = new Date();
        setRow(updatedRow);
      }
    } catch (error) {
      console.error('Error viewing PDF:', error);
    }
  };

  return (
    <div className={classes.viewSection}>
      <Button
        data-testid="caseRecordViewButton"
        variant="contained"
        size="small"
        className={classes.viewBtn}
        onClick={onOpenButtonClick}
        disabled={!isPdfFile}
      >
        {isViewingFile ? (
          <CircularProgress color="inherit" size={24} thickness={8} />
        ) : (
          'View'
        )}
      </Button>
      <Button
        data-testid="caseRecordDownloadButton"
        variant="contained"
        size="small"
        className={classes.viewBtn}
        disabled={!row['fileName'] || disableDownloadButton}
        onClick={onDownloadButtonClick}
      >
        {btnDownloading ? (
          <CircularProgress
            color="inherit"
            size={24}
            thickness={8}
            variant={variantDownload}
            value={completedDownload}
          />
        ) : (
          'Download'
        )}
      </Button>
    </div>
  );
}

ViewButton.propTypes = {
  classes: PropTypes.object.isRequired,
  row: PropTypes.shape({
    id: PropTypes.string.isRequired,
    fileName: PropTypes.string,
    lastReviewedAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
      PropTypes.instanceOf(moment),
    ]),
  }), // Not required here because it's provided by EnhancedTable at runtime
  setRow: PropTypes.func, // Not required here because it's provided by EnhancedTable at runtime
  isDownloadAllowed: PropTypes.bool,
  recordsInfo: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    orderRecordId: PropTypes.string.isRequired,
  }).isRequired,
};

ViewButton.defaultProps = {
  isDownloadAllowed: true,
};

const RecordsDocumentsDialog = (props) => {
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);

  const { classes, title, id, open, setOpen, recordsInfo, isDownloadAllowed } =
    props;

  const handleGetExpertRecordDocuments = useCallback(async () => {
    if (open && recordsInfo) {
      try {
        setLoading(true);
        const response = await getExpertRecordDocuments(
          recordsInfo['orderId'],
          recordsInfo['orderRecordId']
        );
        setDocuments(response.data);
        setLoading(false);
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    }
  }, [recordsInfo, open]);

  useEffect(() => {
    handleGetExpertRecordDocuments();
  }, [handleGetExpertRecordDocuments]);

  const customCells = recordsInfo ? {
    actions: (
      <ViewButton classes={classes} recordsInfo={recordsInfo} isDownloadAllowed={isDownloadAllowed} />
    ),
  } : {};

  const handleClose = () => {
    setOpen(false);
    setDocuments([]);
  };
  return (
    <Dialog
      open={open}
      id={id}
      onClose={handleClose}
      aria-labelledby="record-documents-dialog"
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
    >
      <DialogTitle
        id="record-documents-dialog"
        className={classes.dialogHeader}
      >
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {title}
          <IconButton
            aria-label="close"
            data-testid="close-button"
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Grid>
      </DialogTitle>
      <DialogContent className={classes.dialogBody}>
        {recordsInfo?.headerData && (
          <Box style={{ marginBottom: '10px' }}>
            {recordsInfo?.headerData?.map((p, i) => {
              const rowClass = (i + 1) % 2 === 0 ? classes.even : classes.odd;
              return (
                <Box className={rowClass} key={p.label}>
                  <Typography style={{ padding: 10, fontSize: '16px' }}>
                    <Typography
                      component="span"
                      className={classes.headerTitle}
                    >
                      {p.label}:
                    </Typography>
                    {p.value}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
        <EnhancedTable
          rows={documents}
          headCells={headCells}
          customCells={customCells}
          rowsPerPageOptions={[]}
          showPagination
          pageCount={5}
          isLoading={loading}
        />
      </DialogContent>
    </Dialog>
  );
};

RecordsDocumentsDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  recordsInfo: PropTypes.shape({
    orderId: PropTypes.string.isRequired, // GUID from C#
    orderRecordId: PropTypes.string.isRequired,
    orderNumber: PropTypes.string, // String from C#
    headerData: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string,
      })
    ),
  }),
  isDownloadAllowed: PropTypes.bool,
};

RecordsDocumentsDialog.defaultProps = {
  recordsInfo: null,
  isDownloadAllowed: true,
};

export default withStyles(styles)(RecordsDocumentsDialog);
